/** @jsx jsx */
import React, { useMemo, ReactElement } from 'react';
import { jsx } from '@emotion/core';
import { Grid } from '@material-ui/core';
import PageContainer from 'components/PageContainer';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';
import styles from './styles';
import { Wallet } from 'state/instantCredit';
import LoadingScreen from 'components/LoadingScreen';
import { partition } from 'ramda';

interface MerchantChoicePropTypes {
  merchant: Wallet;
  onChoice: (wallet: Wallet) => void;
}

const MerchantChoice: React.FC<MerchantChoicePropTypes> = props => {
  const {
    merchant: { consumerFacingEntityName, assetData, assetType },
    merchant,
    onChoice,
  } = props;
  return (
    <div css={styles.merchantChoice} onClick={() => onChoice(merchant)}>
      <div css={styles.merchantBrand}>
        <div css={styles.merchantIcon}>
          <img
            alt={consumerFacingEntityName}
            aria-hidden="true"
            src={`data:${assetType};base64,${assetData}`}
            height="40"
          />
        </div>
        <div css={styles.merchantLabel}>{consumerFacingEntityName}</div>
      </div>
      <ChevronRightIcon />
    </div>
  );
};

interface PropTypes {
  inProgress: boolean;
  wallets: Wallet[];
  onChoice: (wallet: Wallet) => void;
}

interface WalletListPropTypes {
  wallets: Wallet[];
  title: ReactElement;
  onChoice: PropTypes['onChoice'];
}
const WalletList: React.FC<WalletListPropTypes> = ({
  wallets,
  title,
  onChoice,
}) => {
  if (wallets.length === 0) {
    return null;
  }
  return (
    <Grid container justify="center">
      <React.Fragment>
        <Grid item xs={12}>
          <div css={styles.sectionTitle}>{title}</div>
        </Grid>

        <Grid item xs={12}>
          {wallets.map(wallet => (
            <MerchantChoice
              key={wallet.walletId}
              onChoice={onChoice}
              merchant={wallet}
            />
          ))}
        </Grid>
      </React.Fragment>
    </Grid>
  );
};

const AddToMerchantsView: React.FC<PropTypes> = props => {
  const { wallets, onChoice, inProgress } = props;
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();

  const [merchants, merchantWallets] = useMemo(
    () => partition(merchant => merchant.walletType === 'MERCHANT', wallets),
    [wallets],
  );

  return (
    <React.Fragment>
      {inProgress ? (
        <LoadingScreen />
      ) : (
        <PageContainer maxWidth="sm">
          {wallets.length === 0 ? (
            <div css={styles.emptyMessage}>
              {tMdMultiline('pushProvisioning.merchantList.emptyMessage')}
            </div>
          ) : (
            <React.Fragment>
              <WalletList
                title={tMd('pushProvisioning.merchantList.walletInfo')}
                wallets={merchants}
                onChoice={onChoice}
              />
              <WalletList
                title={tMd('pushProvisioning.merchantList.merchantInfo')}
                wallets={merchantWallets}
                onChoice={onChoice}
              />
            </React.Fragment>
          )}
        </PageContainer>
      )}
    </React.Fragment>
  );
};

export default AddToMerchantsView;
